<!--
  App.vue - Root Application Component

  This is the main application component that provides the layout structure for the entire website.

  Features:
  - Top navigation bar with university logo and menu tabs
  - Router view container for page content
  - Active tab tracking synchronized with route changes
  - Responsive layout with Vuetify components

  Structure:
  - v-app-bar: Top navigation with logo and tabs
  - v-container.page-container: Main content area with max-width constraint
  - router-view: Dynamic content based on current route
-->

<template>
  <v-app>
    <v-main>
      <v-container class="d-flex">
        <!-- <v-app-bar app class="centered-app-bar"> -->
        <v-app-bar app>
          <!-- University of St. Gallen logo -->
          <v-img max-height="70" max-width="150" contain src="./views/images/hsg_logo.png"></v-img>

          <!-- Navigation tabs -->
          <v-tabs v-model="activeTab" mandatory>
            <v-tab @click="navigateTo('/')">Home</v-tab>
            <v-tab @click="navigateTo('/people')">People</v-tab>
            <v-tab @click="navigateTo('/research')">Research</v-tab>
            <v-tab @click="navigateTo('/publications')">Publications</v-tab>
            <v-tab @click="navigateTo('/media')">In the Media</v-tab>
            <v-tab @click="navigateTo('/courses')">Courses</v-tab>
            <v-tab @click="navigateTo('/thesis')">Theses</v-tab>
            <v-tab @click="navigateTo('/contact')">Contact</v-tab>
          </v-tabs>
        </v-app-bar>
      </v-container>

      <!-- Main content container with max-width constraint -->
      <v-container class="page-container">
        <router-view />
      </v-container>

    </v-main>
  </v-app>
</template>

<script>
/**
 * App Component
 *
 * Root component that manages:
 * - Navigation tabs and active state
 * - Route mapping to tab indices
 * - Global layout and styling
 */
export default {

  name: "App",
  components: {},
  data: () => ({
    activeTab: 0,  // Currently active tab index

    // Maps route paths to tab indices for active state management
    routeToTabIndex: {
      '/': 0,
      '/people': 1,
      '/research': 2,
      '/publications': 3,
      '/media': 4,
      '/courses': 5,
      '/thesis': 6,
      '/contact': 7,
    },

  }),
  created() {
    // Highlight the correct tab on initial load / refresh
    this.syncActiveTab();
  },
  watch: {
    // Keep the highlighted tab in sync with the URL (refresh, back/forward, direct links)
    $route() {
      this.syncActiveTab();
    },
  },
  methods: {
    /**
     * Set the active tab based on the current route's path.
     * Matches by longest path prefix so sub-routes (e.g. /people/:id) still
     * highlight their parent tab (People).
     */
    syncActiveTab() {
      const path = this.$route.path;
      if (path === '/') {
        this.activeTab = 0;
        return;
      }
      const match = Object.keys(this.routeToTabIndex)
        .filter(p => p !== '/' && path.startsWith(p))
        .sort((a, b) => b.length - a.length)[0];
      if (match !== undefined) {
        this.activeTab = this.routeToTabIndex[match];
      }
    },
    /**
     * Navigate to a route and update active tab
     * @param {string} routePath - The route path to navigate to
     */
    navigateTo(routePath) {

      this.activeTab = this.routeToTabIndex[routePath];
    this.$router.push(routePath).catch(err => {
      // Ignore duplicate navigation errors
      if (err.name !== "NavigationDuplicated" && !err.message.includes("Avoided redundant navigation to current location")) {
        console.log(err);
      }
    });
    },
  },
};
</script>

<style scoped>

.centered-app-bar {
  display: flex;
  justify-content: center;
}



.name {
  font-family: "Helvetica Neue";
  size: "16px";
  color: #555;
}

.nav-tabs .nav-link:focus {
  outline: none;
}

.underline {
  text-decoration: underline;
  font-family: "Helvetica Neue";
  color: #555;
}

.routerLink {
  text-decoration: none;
}

.wrap-text {
  -webkit-line-clamp: unset !important;
}

.v-list-item {
  flex: 0;
}

.titlePaper {
  color: #0e0ca6;
  font-family: "Arial";
  font-size: 18px;
}

.menu {
  color: #1a0dab;
  font-family: "Roboto";
  font-size: 17px;
}

.subtitle {
  color: #337ab7;
}

.avatarimg {
  border: 4px solid #ddd !important;
  display: block;
  background: #ffffff;
}

.page-container {
  max-width: 1200px !important;
}

.navbar {
  background: #f44336;
  padding: 1rem;
  font-size: 1.5rem;
  border-bottom: 1px solid white;
}

@import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Rubik:wght@368&family=Space+Grotesk:wght@300&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400&display=swap");

html,
body {
  height: 100%;
  padding-top: 10px;
  background: #e6ecf1;
}

#app .r {
  font-family: "Rubik", sans-serif;
}

#app .button {
  margin-bottom: 20px;
}

#app .img {
  border-radius: 30px;
}

#app .level-item {
  padding-left: 10px;
  color: #00d1b2;
}

#app input:focus {
  border-color: #00d1b2;
}
</style>
